import React from 'react';
import { motion } from 'framer-motion';
import AppTile from './AppTile';
import './AppGrid.css';

// AppGrid Component - Container for app tiles in a section
// Manages the grid layout and staggered animation of tiles
// ARCHITECTURE: Passes userRole to AppTile for token generation
// Animation: Section title animates in, followed by cascading tiles

interface App {
  name: string;
  type: string;
  logo: string;
  status: string;
  category: string;
  moduleUrl?: string;
}

interface Section {
  title: string;
  subtitle: string;
  apps: App[];
}

interface AppGridProps {
  section: Section;
  index: number;
}

const AppGrid: React.FC<AppGridProps> = ({ section, index }) => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, delay: 0.5 + index * 0.2 }
    }
  };

  const titleVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.4 }
    }
  };

  const gridVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08,
        delayChildren: 0.2
      }
    }
  };

  return (
    <motion.section
      className="apps-section"
      variants={sectionVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Section Header */}
      <motion.div className="section-title" variants={titleVariants}>
        <span className="section-icon">
          {section.title.split(' ')[0]}
        </span>
        <span>{section.title}</span>
      </motion.div>

      {/* App Tiles Grid */}
      <motion.div
        className="app-grid"
        variants={gridVariants}
        initial="hidden"
        animate="visible"
      >
        {section.apps.map((app, appIdx) => (
          <AppTile key={appIdx} app={app} index={appIdx} />
        ))}
      </motion.div>
    </motion.section>
  );
};

export default AppGrid;
